import { Box, Container, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import makeStyles from '@mui/styles/makeStyles'
import { useIntl } from 'react-intl'
import { LinkStyleType, LinkType, StyledLink } from '.'

type UnionContactDetailsProps = {
  city: string
  email: string
  phone: string
  postCode: string
  postName: string
  street: string
  link?: { label: string; url: string }
}

type MembershipServiceContactDetailsProps = {
  title?: string
  email?: string
  phone?: string
}

type UnionNameProps = {
  primaryName: string
  secondaryName: string
}

type UnionTermsOfUsageProps = {
  termsDescription: string
  termsDescriptionLinkLabel: string
  termsDescriptionLinkUrl: string
}

const useStyles = makeStyles((theme) => ({
  bottomBar: {
    backgroundColor: theme.palette.bottomBar.main,
    padding: '2rem'
  },
  bottomBarHideMobile: {
    display: 'none',
    [theme.breakpoints.up('md')]: {
      display: 'flex'
    }
  }
}))

const UnionContactDetailLine = styled(Typography)({
  paddingBottom: '0.2rem'
})

const UnionTermsDescription = styled(Typography)({})

const UnionNamePrimary = styled(Typography)(({ theme }) => ({
  fontSize: theme.variables.fontSizes.unionName.primary.fontSize,
  fontWeight: 'bold',
  paddingRight: '1rem'
}))

const UnionNameSecondary = styled(Typography)(({ theme }) => ({
  fontSize: theme.variables.fontSizes.unionName.secondary.fontSize,
  fontWeight: 'bold'
}))

const UnionContactDetails = (props: UnionContactDetailsProps) => {
  const { city, email, phone, postCode, postName, street, link } = props

  return (
    <Box
      display="flex"
      flexDirection="column"
      pb={4}
      id="union-contact-details"
    >
      <UnionContactDetailLine>{postName}</UnionContactDetailLine>
      <UnionContactDetailLine>
        {`${street}, ${postCode} ${city}`}
      </UnionContactDetailLine>
      <UnionContactDetailLine>{phone}</UnionContactDetailLine>
      {email !== 'noEmail' && (
        <UnionContactDetailLine>{email}</UnionContactDetailLine>
      )}
      {link && (
        <StyledLink
          isUnderlined
          href={link.url}
          rel="noreferrer"
          target="blank"
          type={LinkType.BASIC}
        >
          {link.label}
        </StyledLink>
      )}
    </Box>
  )
}

const MembershipServiceContactDetails = (
  props: MembershipServiceContactDetailsProps
) => {
  const { title, email, phone } = props

  return (
    <Box
      display="flex"
      flexDirection="column"
      pb={4}
      id="membership-service-contact-details"
    >
      <UnionContactDetailLine>{title}</UnionContactDetailLine>
      <UnionContactDetailLine>{phone}</UnionContactDetailLine>
      <UnionContactDetailLine>{email}</UnionContactDetailLine>
    </Box>
  )
}

const UnionName = (props: UnionNameProps) => {
  const { primaryName, secondaryName } = props
  return (
    <Box
      alignItems="baseline"
      display="flex"
      flexDirection="row"
      pb={2}
      id="union-name"
    >
      {primaryName && primaryName.trim() && (
        <UnionNamePrimary>{primaryName}</UnionNamePrimary>
      )}
      {secondaryName && secondaryName.trim() && (
        <UnionNameSecondary>{secondaryName}</UnionNameSecondary>
      )}
    </Box>
  )
}

const UnionTermsOfUsage = (props: UnionTermsOfUsageProps) => {
  const {
    termsDescription,
    termsDescriptionLinkLabel,
    termsDescriptionLinkUrl
  } = props
  return (
    <Box display="flex" flexDirection="column">
      <Box pb={2}>
        <UnionTermsDescription>{termsDescription}</UnionTermsDescription>
      </Box>
      <Box display="flex" justifyContent="flex-end">
        <StyledLink
          isUnderlined
          href={termsDescriptionLinkUrl}
          linkStyleType={LinkStyleType.IMPORTANT}
          rel="noreferrer"
          target="blank"
          type={LinkType.BASIC}
        >
          {termsDescriptionLinkLabel}
        </StyledLink>
      </Box>
    </Box>
  )
}

interface BottomBarProps {
  showMobile: boolean
}

export const BottomBar = ({ showMobile }: BottomBarProps) => {
  const { formatMessage } = useIntl()
  const classes = useStyles()
  const linkLabel = formatMessage({
    id: 'contact.link.label'
  })
  const linkUrl = formatMessage({
    id: 'contact.link.url'
  })
  const link =
    linkUrl !== 'contact.link.url'
      ? { label: linkLabel, url: linkUrl }
      : undefined

  const membershipServiceTitle = formatMessage({
    id: 'contact.membershipServiceDetails.title'
  })
  const membershipServiceEmail = formatMessage({
    id: 'contact.membershipServiceDetails.email'
  })
  const membershipServicePhone = formatMessage({
    id: 'contact.membershipServiceDetails.phone'
  })

  return (
    <Box
      className={`${classes.bottomBar} ${
        showMobile ? '' : classes.bottomBarHideMobile
      }`}
    >
      <Container maxWidth="md">
        <Box display="flex" flexDirection="column">
          <UnionName
            primaryName={formatMessage({ id: 'union.name' })}
            secondaryName={formatMessage({ id: 'union.description' })}
          />
          <UnionContactDetails
            city={formatMessage({ id: 'contact.address.city' })}
            email={formatMessage({ id: 'contact.email' })}
            phone={formatMessage({ id: 'contact.phone' })}
            postCode={formatMessage({ id: 'contact.address.postCode' })}
            postName={formatMessage({ id: 'contact.address.postName' })}
            street={formatMessage({ id: 'contact.address.street' })}
            link={link}
          />
          {membershipServiceTitle !== ' ' && (
            <MembershipServiceContactDetails
              title={membershipServiceTitle}
              email={membershipServiceEmail}
              phone={membershipServicePhone}
            />
          )}
          <UnionTermsOfUsage
            termsDescription={formatMessage({ id: 'terms.description' })}
            termsDescriptionLinkLabel={formatMessage({
              id: 'terms.link.label'
            })}
            termsDescriptionLinkUrl={formatMessage({ id: 'terms.link.url' })}
          />
        </Box>
      </Container>
    </Box>
  )
}
